import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Google Sign-In, deliberately optional.
 *
 * Nothing in the product requires an account — you can calculate, buy and
 * download while signed out. Signing in only adds continuity: your bands follow
 * you to another device without hunting for a link in an inbox.
 *
 * The Google script is loaded lazily and only when the backend reports sign-in
 * is configured, so an unconfigured deployment pulls no third-party code at all.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'sab.session';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

const AuthContext = createContext({
  enabled: false, user: null, token: null, gsiReady: false,
  signIn: () => {}, signOut: () => {}, claimBand: async () => {},
  renderButton: () => false,
});

export const useAuth = () => useContext(AuthContext);

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function writeToken(t) {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
}

export function AuthProvider({ children }) {
  const [config, setConfig] = useState({ enabled: false, google_client_id: null });
  const [token, setToken] = useState(readToken);
  const [user, setUser] = useState(null);
  const scriptRef = useRef(null);
  const [gsiReady, setGsiReady] = useState(false);

  // Ask the backend whether sign-in exists before touching Google at all.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/config`)
      .then(({ data }) => { if (!cancelled) setConfig(data); })
      .catch(() => { /* stays disabled; sign-in is never load-bearing */ });
    return () => { cancelled = true; };
  }, []);

  // Restore the session. A rejected token is dropped rather than retried.
  useEffect(() => {
    if (!token) { setUser(null); return; }
    let cancelled = false;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data.signed_in) setUser(data.user);
        else { writeToken(null); setToken(null); setUser(null); }
      })
      .catch(() => { if (!cancelled) { writeToken(null); setToken(null); setUser(null); } });
    return () => { cancelled = true; };
  }, [token]);

  const handleCredential = useCallback(async (response) => {
    try {
      const { data } = await axios.post(`${API}/auth/google`, {
        credential: response.credential,
      });
      writeToken(data.token);
      setToken(data.token);
      setUser(data.user);
    } catch {
      writeToken(null); setToken(null); setUser(null);
    }
  }, []);

  // Load Google Identity Services once, and only if it is actually configured.
  useEffect(() => {
    if (!config.enabled || !config.google_client_id || scriptRef.current) return;

    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    const init = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: config.google_client_id,
        callback: handleCredential,
        use_fedcm_for_prompt: true,
      });
      setGsiReady(true);
    };

    if (existing) { scriptRef.current = existing; init(); return; }

    const el = document.createElement('script');
    el.src = GSI_SRC;
    el.async = true;
    el.defer = true;
    el.onload = init;
    document.head.appendChild(el);
    scriptRef.current = el;
  }, [config, handleCredential]);

  /**
   * Render Google's own button into an element.
   *
   * Deliberately NOT google.accounts.id.prompt(). That is the One Tap flow,
   * which browsers increasingly suppress — cooldowns after a dismissal, and
   * third-party cookie restrictions — and when it is suppressed it fails
   * SILENTLY, so the user just sees nothing happen. renderButton always works.
   */
  const renderButton = useCallback((el) => {
    if (!el || !window.google?.accounts?.id) return false;
    el.innerHTML = '';
    window.google.accounts.id.renderButton(el, {
      theme: 'filled_black',
      size: 'medium',
      shape: 'pill',
      text: 'signin',
      logo_alignment: 'left',
    });
    return true;
  }, []);

  // Kept as a fallback for callers that still want One Tap.
  const signIn = useCallback(() => {
    if (window.google?.accounts?.id) window.google.accounts.id.prompt();
  }, []);

  const signOut = useCallback(() => {
    writeToken(null);
    setToken(null);
    setUser(null);
    try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch { /* noop */ }
  }, []);

  /** Attach a band made while signed out to the account. Silent on failure. */
  const claimBand = useCallback(async (bandId) => {
    if (!token || !bandId) return false;
    try {
      const { data } = await axios.post(
        `${API}/zenith/band/${bandId}/claim`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return !!data.claimed;
    } catch { return false; }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{ enabled: config.enabled, user, token, signIn, signOut, claimBand, renderButton, gsiReady }}
    >
      {children}
    </AuthContext.Provider>
  );
}
